/**
 * Bootstrap — the wiring that turns eight independent modules into a game.
 *
 * Everything interesting lives elsewhere; this file's whole job is ordering and
 * plumbing. The parts worth knowing:
 *
 * - **The relay connects before the start screen does.** Ghosts are climbing
 *   behind the title card, which is the cheapest possible advertisement for the
 *   multiplayer, and it means the CLIMB button is never waiting on a socket.
 * - **Physics never stops.** Losing pointer lock, opening the pause menu or
 *   alt-tabbing feeds the simulation a *neutral* input rather than freezing it.
 *   This is a foddian game: there is no free pause halfway through a fall.
 * - **Respawn is not always the plaza.** Landing on a checkpoint pad moves the
 *   respawn point up the mountain, and falling `CHECKPOINT_DROP_M` below that pad
 *   returns you to it. The highest pad survives a reload (pinned to the level
 *   hash, so a regenerated mountain discards it); restarting from the pause menu
 *   gives them all back, saved one included.
 * - **Altitude is measured from the spawn**, not from world zero, everywhere the
 *   player can see a number — including the ghost leaderboard, whose wire values
 *   are raw world Y and have to be rebased here.
 * - The render callback is hot. It reuses arrays, compares before it writes and
 *   only builds a debug record when the overlay is actually on screen.
 */

import { Vector3 } from 'three';

import { LEVEL_SEED, PALETTE } from '@shared/constants';
import { buildLevel, hashLevel, ZONE_NAMES } from '@shared/level';
import type { V3 } from '@shared/primitives';

import { audio } from './audio/audio';
import { Input } from './core/input';
import { FixedLoop } from './core/loop';
import { COLOR_AUTO, settings } from './core/settings';
import { TouchControls, isTouchDevice } from './core/touch';
import { NetClient, type NetStatus } from './net/client';
import { GhostManager } from './net/ghosts';
import { Broadphase } from './physics/broadphase';
import { MoverSystem } from './physics/moverSystem';
import { PogoBody, TUNING, type BounceEvent, type FrameInput } from './physics/pogo';
import { OrbitFollowCamera } from './render/camera';
import {
  buildCheckpointBeacon,
  buildLevelMeshes,
  buildPbRing,
  updateMoverMeshes,
} from './render/levelMesh';
import type { SkeletonRig } from './render/rigTypes';
import { createScene, updateShadowTarget, updateSky } from './render/scene';
import { createSkeleton } from './render/skeleton';
import { DebugOverlay } from './ui/debug';
import { Hud, type HudRosterEntry } from './ui/hud';
import { isPauseOpen, showPauseMenu, showStartScreen } from './ui/startScreen';
import { clearToasts, screenShake, toast } from './ui/toast';

// ---------------------------------------------------------------------------
// tuning that belongs to the integration, not to any one module
// ---------------------------------------------------------------------------

/** Fall distances that earn a toast, in metres. */
const FALL_MINOR_M = 8;
const FALL_MAJOR_M = 40;
/** Dropping this far below the session's high-water mark earns the cruel one. */
const PROGRESS_LOSS_M = 25;

/** Below this altitude you have left the level; take the respawn. */
const VOID_Y = -40;

/** Fall this far below the active checkpoint pad and you return to it. */
const CHECKPOINT_DROP_M = 25;
/** Snap radius for matching a checkpoint landing to its pad entry. */
const CHECKPOINT_MATCH_M = 4;

/** Hide the personal-best ring until the best is worth marking. */
const PB_RING_MIN_M = 5;
/** At most one `localStorage` write per this many ms while a new best climbs. */
const PB_WRITE_MS = 1000;

/** Summit: within this of `topY`, and this close to the shaft axis. */
const SUMMIT_TOLERANCE_M = 2;
const SUMMIT_RADIUS_M = 12;
/** Fall this far below the summit and it counts as a fresh ascent. */
const SUMMIT_REARM_M = 30;

/** The leaderboard is social pressure, not telemetry — 5 Hz is plenty. */
const ROSTER_REFRESH_MS = 200;

/** Radians per second the camera drifts around the mountain on the title screen. */
const TITLE_ORBIT_RATE = 0.09;
const TITLE_ORBIT_RADIUS = 62;
const TITLE_ORBIT_Y = 52;
const TITLE_LOOK_Y = 74;

/** Clamp on the wall-clock delta handed to the camera, so a stall cannot snap it. */
const MAX_RENDER_DT = 0.1;

/**
 * Pixels of camera turn per pixel of finger drag. A thumb crosses maybe a fifth
 * of the screen before it runs out of hand; a mouse swipe has no such limit, so
 * touch look needs the multiplier to reach the same view in one gesture. The
 * player's sensitivity slider still multiplies on top of this, inside the camera.
 */
const TOUCH_LOOK_SCALE = 2.5;

// ---------------------------------------------------------------------------
// level + scene
// ---------------------------------------------------------------------------

const { renderer, scene, camera, sun, hemi, onResize } = createScene();
const canvas = renderer.domElement;

const level = buildLevel(LEVEL_SEED);
/** Also the key a saved checkpoint is pinned to — see `settings.checkpoint`. */
const levelHash = hashLevel(level);
// The determinism check from §Verification: two clients must log the same line.
console.log(
  `[level] seed ${LEVEL_SEED} · ${level.primitives.length} primitives · hash ${levelHash} · top ${level.topY} m`,
);

// Strictly after the hash: the hash is taken from rest poses, and the mover
// system is the only thing in the client that ever moves a primitive off one.
const moverSystem = new MoverSystem(level);
const broadphase = new Broadphase(level.primitives, moverSystem);
const { group: levelGroup, dynamic: moverMeshes } = buildLevelMeshes(level);
scene.add(levelGroup);

const spawnY = level.spawn[1];

/** Personal best in metres above the spawn. Mirrored to `settings` at ≤1 Hz. */
let best = settings.best;
let bestDirty = false;
let bestWrittenAt = 0;

const pbRing = buildPbRing();
pbRing.position.y = spawnY + best;
pbRing.visible = best >= PB_RING_MIN_M;
scene.add(pbRing);

/** Beam over the checkpoint you would return to. Hidden until one is claimed. */
const beacon = buildCheckpointBeacon();
beacon.visible = false;
scene.add(beacon);

// ---------------------------------------------------------------------------
// player
// ---------------------------------------------------------------------------

const pogo = new PogoBody(level, broadphase, moverSystem);

/**
 * Provisional tint: the server has the final say (it assigns the least-used hue
 * for `COLOR_AUTO`, and may refuse a taken one), but the skeleton has to exist
 * before the handshake or there is nothing on screen behind the title card.
 */
let localTint = settings.color !== COLOR_AUTO ? settings.color : (PALETTE[0] ?? 0xede6d2);
let rig: SkeletonRig = createSkeleton(localTint);
rig.setNameTag(null); // you already know who you are
scene.add(rig.root);

/**
 * Re-tint the local skeleton. The rig bakes its hue into cloned materials at
 * construction, so "re-tint" means "rebuild" — which is fine, because this runs
 * at most once per connection.
 */
function setLocalTint(color: number): void {
  if (color === localTint) return;
  localTint = color;
  rig.dispose();
  rig = createSkeleton(color);
  rig.setNameTag(null);
  scene.add(rig.root);
}

const input = new Input();
/**
 * Null on anything with a real mouse. A phone has no pointer lock and no keys,
 * so where the desktop build gates on `isLocked` this build gates on "the touch
 * overlay exists" — see `fixedUpdate`.
 */
const touch = isTouchDevice() ? new TouchControls() : null;
if (touch !== null) document.body.classList.add('sk-touch-mode');

const orbitCam = new OrbitFollowCamera(camera, broadphase);
orbitCam.setSensitivityScale(settings.sensitivity);

// ---------------------------------------------------------------------------
// ghosts, UI
// ---------------------------------------------------------------------------

const ghosts = new GhostManager(scene, createSkeleton);
const net = new NetClient();
const hud = new Hud();
const debug = new DebugOverlay();

/** Names by player id, so a `left` toast can say who it was. */
const ghostNames = new Map<number, string>();

let netStatus: NetStatus = 'offline';

// ---------------------------------------------------------------------------
// run state
// ---------------------------------------------------------------------------

/** False until CLIMB is pressed: the camera is still orbiting the mountain. */
let started = false;
/** Highest altitude reached since the last progress-loss toast. */
let sessionPeak = 0;
/** Cleared once the summit is reached; re-armed by falling back down. */
let summitArmed = true;
/** Index into `level.checkpoints` of the highest pad claimed; -1 is the plaza. */
let activeCheckpoint = -1;
/** Mirrors the charge button, for the audio sweep (the pogo keeps it private). */
let chargingNow = false;

/**
 * Move the respawn point onto checkpoint `idx` and light its beacon. Shared by a
 * fresh claim and by one restored from storage, so the two cannot drift apart.
 */
function activateCheckpoint(idx: number): void {
  const c = level.checkpoints[idx] as V3;
  activeCheckpoint = idx;
  // 1.5 m up, the same clearance the plaza spawn stands at.
  pogo.setRespawn([c[0], c[1] + 1.5, c[2]]);
  beacon.position.set(c[0], c[1], c[2]);
  beacon.visible = true;
}

/**
 * @param restore
 *   `true` only on the boot reset, where a checkpoint saved by a previous visit
 *   may be reclaimed. The pause menu's restart passes `false`: that one is the
 *   total reset, and the saved pad goes back to the mountain with the rest.
 */
function resetRun(restore = false): void {
  const saved = restore ? settings.checkpoint : null;
  // A save from a different level layout points at a pad that no longer exists
  // where it used to — discard it rather than respawn the player inside rock.
  const resume =
    saved !== null && saved.h === levelHash && saved.i < level.checkpoints.length ? saved.i : -1;

  if (resume >= 0) {
    activateCheckpoint(resume);
  } else {
    pogo.clearRespawn();
    activeCheckpoint = -1;
    beacon.visible = false;
    if (!restore) settings.checkpoint = null;
  }
  pogo.respawn();
  orbitCam.reset(pogo.pos);
  // Starting partway up the mountain is not progress this session earned, but it
  // is height already banked — seed the mark with it, or the first landing bills
  // the player for metres they never climbed.
  sessionPeak = resume >= 0 ? pogo.pos.y - spawnY : 0;
  summitArmed = true;
  clearToasts();
  if (resume >= 0) toast(`Resumed at checkpoint ${resume + 1}`, 'progress');
}

// ---------------------------------------------------------------------------
// dev self-tests
// ---------------------------------------------------------------------------

if (import.meta.env.DEV) {
  void import('./physics/collision.test').then(({ runCollisionSelfTest }) => {
    const failures = runCollisionSelfTest();
    if (failures.length === 0) console.log('[collision] self-test passed');
    else for (const f of failures) console.error(`[collision] ${f}`);
  });

  // A console handle for the tuning pass: live physics constants and a teleport.
  (globalThis as unknown as Record<string, unknown>)['skelestick'] = {
    pogo,
    level,
    TUNING,
    teleport(y: number, x = 0, z = -16): void {
      pogo.pos.set(x, y, z);
      pogo.vel.set(0, 0, 0);
      orbitCam.reset(pogo.pos);
    },
  };
}

// ---------------------------------------------------------------------------
// landings: audio, toasts, the accounting of failure
// ---------------------------------------------------------------------------

pogo.onBounce = (e: BounceEvent): void => {
  const mushroom = (e.material.bounceMultiplier ?? 1) > 1.2;
  audio.bounce(e.speed, mushroom);

  const fell = Math.round(e.fallDistance);
  if (e.fallDistance > FALL_MAJOR_M) {
    toast(`YOU FELL ${fell} METRES`, 'major');
    screenShake();
    audio.sting();
  } else if (e.fallDistance > FALL_MINOR_M) {
    toast(`Fell ${fell} m`, 'minor');
  }

  // Progress loss is measured against the session high-water mark, and only the
  // landing that *ends* a fall can report it — resetting the mark afterwards is
  // what keeps a long tumble from firing this on every bounce down the funnel.
  const heightM = pogo.pos.y - spawnY;
  const lost = sessionPeak - heightM;
  if (lost > PROGRESS_LOSS_M) {
    toast(`You lost ${Math.round(lost)} m of progress.`, 'progress');
    sessionPeak = heightM;
  }

};

// ---------------------------------------------------------------------------
// checkpoints — claimed off the pogo's contact ledger, not off BounceEvent:
// landings under BOUNCE_EVENT_MIN fire no event, and the idle pogo touches down
// quietly four times a second, so a gentle arrival on a pad must still count.
// ---------------------------------------------------------------------------

/** The last `pogo.landingSeq` the claim check has seen. */
let claimedLandingSeq = 0;

/**
 * Claim the pad nearest `point`, if any is close enough and higher than the one
 * we hold. The pads' corner posts carry the same material as the disc, so a
 * landing can be a metre or two off the entry it belongs to — hence the generous
 * match radius. Only ever ratchets upward: coming back down to a pad you already
 * own, or to one below it, is not an event.
 */
function tryClaimCheckpoint(point: Vector3): void {
  const pads = level.checkpoints;
  let idx = -1;
  let bestD = CHECKPOINT_MATCH_M;
  for (let i = 0; i < pads.length; i++) {
    const c = pads[i] as V3;
    const d = Math.hypot(point.x - c[0], point.y - c[1], point.z - c[2]);
    if (d < bestD) {
      bestD = d;
      idx = i;
    }
  }
  if (idx > activeCheckpoint) {
    activateCheckpoint(idx);
    // Pinned to the hash: the index alone would be a lie on a different mountain.
    settings.checkpoint = { h: levelHash, i: idx };
    // A pad is the door into the zone above it, so the toast names where you
    // have arrived rather than where you have been.
    toast(`${ZONE_NAMES[idx + 1]} — CHECKPOINT ${idx + 1} of ${pads.length}`, 'progress');
    audio.sting();
  }
}

// ---------------------------------------------------------------------------
// networking
// ---------------------------------------------------------------------------

net.on('status', (s) => {
  netStatus = s;
});

net.on('welcome', (w) => {
  ghosts.handleWelcome(w);
  ghostNames.clear();
  for (const p of w.players) ghostNames.set(p.id, p.name);
  // The server owns colour assignment; whatever it handed back is who we are.
  setLocalTint(w.you.color);
  console.log(
    `[net] welcome id=${w.id} name=${w.you.name} color=#${w.you.color.toString(16).padStart(6, '0')} others=${w.players.length}`,
  );
});

net.on('joined', (p) => {
  ghosts.handleJoined(p);
  ghostNames.set(p.id, p.name);
  toast(`${p.name} joined the climb`, 'minor');
});

net.on('left', (id) => {
  const name = ghostNames.get(id);
  ghosts.handleLeft(id);
  ghostNames.delete(id);
  toast(`${name ?? 'A climber'} left`, 'minor');
});

net.on('snap', (s) => {
  ghosts.handleSnap(s);
});

/** The identity the current socket joined with, so we know when it is stale. */
let joinedName = settings.name || `bones-${Math.floor(Math.random() * 900 + 100)}`;
let joinedColor: number | undefined = settings.color === COLOR_AUTO ? undefined : settings.color;

// Connect immediately: ghosts should already be climbing behind the title card.
net.connect(joinedName, joinedColor);

// ---------------------------------------------------------------------------
// start screen
// ---------------------------------------------------------------------------

void showStartScreen({
  defaultName: settings.name,
  defaultColor: settings.color,
  connected: () => net.online,
}).then((choice) => {
  // `showStartScreen` has already persisted the name and colour.
  const wantColor = choice.color === COLOR_AUTO ? undefined : choice.color;
  if (choice.name !== joinedName || wantColor !== joinedColor) {
    // The relay ignores a second `join` on an established socket (by design —
    // mid-session identity changes are not a thing), so a new identity means a
    // new socket. `handleWelcome` clears and refills, so no duplicate ghosts.
    joinedName = choice.name;
    joinedColor = wantColor;
    net.disconnect();
    net.connect(joinedName, joinedColor);
  }
  if (wantColor !== undefined) setLocalTint(wantColor);

  audio.init();
  audio.setMuted(settings.mute);

  started = true;
  resetRun(true);
  hud.show();
  // Pointer lock is meaningless on a phone (and rejects noisily); the on-screen
  // controls are what takes its place.
  if (touch !== null) touch.show();
  else input.requestLock(canvas);
});

// ---------------------------------------------------------------------------
// pause / keys / pointer lock
// ---------------------------------------------------------------------------

/** Guards the window between `exitLock()` and the menu actually existing. */
let pausing = false;

function openPause(): void {
  if (!started || pausing || isPauseOpen()) return;
  pausing = true;
  input.exitLock();
  void showPauseMenu({
    onMute: (m) => audio.setMuted(m),
    onSensitivity: (s) => orbitCam.setSensitivityScale(s),
  }).then((action) => {
    pausing = false;
    if (action === 'restart') resetRun();
    // Must stay in the click's microtask chain — pointer lock needs the gesture.
    if (touch === null) input.requestLock(canvas);
  });
}

// Under pointer lock the browser eats Escape to release the lock and never
// delivers the keydown, so the *lock change* is the real pause trigger. The key
// handler covers the other direction: paused-but-unlocked, or lock never granted.
input.onLockChange((locked) => {
  if (!locked && started) openPause();
});
input.onKey('Escape', () => {
  if (!input.isLocked) openPause();
});
input.onKey('Backquote', () => debug.toggle());

// There is no Escape key on a phone, so the overlay carries its own button.
touch?.onPause(openPause);
// A finger the OS stole mid-charge is not a release; releasing would jump.
touch?.onChargeCancel(() => pogo.cancelCharge());

// Re-acquiring pointer lock needs a user gesture, and Chrome refuses for about a
// second after an Escape-release. Any click on the canvas is a second chance.
if (touch === null) {
  canvas.addEventListener('click', () => {
    if (started && !input.isLocked && !isPauseOpen()) input.requestLock(canvas);
  });
}

window.addEventListener('resize', onResize);

/** Never lose a personal best to a closed tab. */
function flushBest(): void {
  if (!bestDirty) return;
  bestDirty = false;
  settings.best = best;
}
window.addEventListener('pagehide', flushBest);
window.addEventListener('beforeunload', flushBest);

// ---------------------------------------------------------------------------
// the loop
// ---------------------------------------------------------------------------

const NEUTRAL: FrameInput = { leanX: 0, leanZ: 0, charging: false };

/** Reused: keyboard and touch summed, for a tablet that has both plugged in. */
const MERGED: FrameInput = { leanX: 0, leanZ: 0, charging: false };

/** @returns a reused object — read it, do not retain it. */
function mergeFrames(kb: FrameInput, tc: FrameInput): FrameInput {
  let x = kb.leanX + tc.leanX;
  let z = kb.leanZ + tc.leanZ;
  // Same reasoning as the diagonal clamp in `Input.frame`: two sources must not
  // out-lean one.
  const m = Math.hypot(x, z);
  if (m > 1) {
    x /= m;
    z /= m;
  }
  MERGED.leanX = x;
  MERGED.leanZ = z;
  MERGED.charging = kb.charging || tc.charging;
  return MERGED;
}

/** Whether the previous fixed step saw live input, to tell a real release apart. */
let wasLive = false;

function fixedUpdate(dt: number): void {
  // First: the body has to resolve against the pose the platforms hold *now*.
  moverSystem.tick(dt);
  // No pointer lock exists on touch, so the overlay's existence is the licence.
  const live = started && !isPauseOpen() && (input.isLocked || touch !== null);
  // Going non-live mid-hold (Esc, alt-tab, lock loss) is not the player letting
  // go of the button — swallow it, or opening the pause menu would fire the jump.
  if (wasLive && !live) pogo.cancelCharge();
  wasLive = live;
  let frame = NEUTRAL;
  if (live) frame = touch !== null ? mergeFrames(input.frame(), touch.frame()) : input.frame();
  chargingNow = frame.charging;
  pogo.update(dt, frame, orbitCam.yaw);
}

// --- title-screen camera ----------------------------------------------------

let titleAngle = 0.6;
const _titleLook = new Vector3(0, TITLE_LOOK_Y, 0);

function orbitTitleCamera(dt: number): void {
  titleAngle += TITLE_ORBIT_RATE * dt;
  camera.position.set(
    Math.sin(titleAngle) * TITLE_ORBIT_RADIUS,
    TITLE_ORBIT_Y,
    Math.cos(titleAngle) * TITLE_ORBIT_RADIUS,
  );
  camera.lookAt(_titleLook);
  // Keep the play camera's yaw in step so the first frame after CLIMB does not
  // whip around — WASD is expressed in this yaw.
  orbitCam.yaw = titleAngle;
}

// --- HUD plumbing -----------------------------------------------------------

/** Reused every frame: the HUD's roster, rebased from world Y to altitude. */
const hudRoster: HudRosterEntry[] = [];
let rosterAt = 0;

function refreshRoster(nowMs: number): void {
  if (nowMs - rosterAt < ROSTER_REFRESH_MS) return;
  rosterAt = nowMs;
  const raw = ghosts.getRoster();
  hudRoster.length = 0;
  for (const g of raw) {
    // `RosterEntry.heightM` is the raw wire Y; the player's readout is altitude
    // above the spawn, and the two must be the same scale or the leaderboard
    // lies by 1.5 m.
    hudRoster.push({ name: g.name, color: g.color, heightM: g.heightM - spawnY });
  }
}

let lastRenderMs = performance.now();

function render(alpha: number, timeSec: number): void {
  const nowMs = performance.now();
  let dt = (nowMs - lastRenderMs) / 1000;
  lastRenderMs = nowMs;
  if (!(dt > 0)) dt = 0;
  else if (dt > MAX_RENDER_DT) dt = MAX_RENDER_DT;

  // --- camera --------------------------------------------------------------
  if (started) {
    const md = input.consumeMouseDelta();
    if (md.dx !== 0 || md.dy !== 0) orbitCam.addMouseDelta(md.dx, md.dy);
    const wheel = input.consumeWheelDelta();
    if (wheel !== 0) orbitCam.addWheelDelta(wheel);
    if (touch !== null) {
      const td = touch.consumeLookDelta();
      if (td.dx !== 0 || td.dy !== 0) {
        orbitCam.addMouseDelta(td.dx * TOUCH_LOOK_SCALE, td.dy * TOUCH_LOOK_SCALE);
      }
      // Already in wheel units, so the zoom range and the slider stay shared.
      const pinch = touch.consumeZoomDelta();
      if (pinch !== 0) orbitCam.addWheelDelta(pinch);
    }
    orbitCam.update(dt, pogo.pos, pogo.vel);
  } else {
    orbitTitleCamera(dt);
  }

  // --- level + skeletons ---------------------------------------------------
  // Same `alpha` as the rig below, so a platform and the body riding it are
  // drawn at the same instant.
  updateMoverMeshes(moverMeshes, moverSystem, alpha);
  rig.applyPose(pogo.getPose(alpha, timeSec));
  // Same `timeSec` as the local rig: every skeleton in the room breathes in phase.
  ghosts.update(nowMs, timeSec, camera.position);
  updateShadowTarget(sun, pogo.pos);
  // The *player's* height, not the camera's: the follow camera biases upward
  // during a fall — as much as 24 m — which would swap the sky for the zone
  // above while you are dropping out of the one you are in. Before the run
  // starts there is no player to read, so the title orbit uses its own height.
  updateSky(scene, hemi, started ? pogo.pos.y : camera.position.y);

  // --- network -------------------------------------------------------------
  // `sendState` is internally rate-limited and delta-gated, so it wants to be
  // called every frame. The `online` guard is only to skip building a state
  // object at all while there is nobody to send it to.
  if (net.online) net.sendState(pogo.getNetState());

  // --- audio ---------------------------------------------------------------
  audio.chargeTick(pogo.charge, chargingNow);
  audio.wind(pogo.vel.y);

  // --- progress accounting -------------------------------------------------
  const heightM = pogo.pos.y - spawnY;
  if (heightM > sessionPeak) sessionPeak = heightM;

  // A new entry in the contact ledger since last frame means a touchdown
  // happened, however quiet; bounces are far slower than frames, so checking
  // only the latest entry never skips one.
  if (pogo.landingSeq !== claimedLandingSeq) {
    claimedLandingSeq = pogo.landingSeq;
    // The lobby floor claims too: the dais is a small target on a wide floor,
    // and landing beside it plainly means "I reached the checkpoint".
    // `tryClaimCheckpoint` still gates on `CHECKPOINT_MATCH_M`, so the far side
    // of the ring is not close enough.
    const tag = pogo.lastLandingMaterial?.tag;
    if (tag === 'checkpoint' || tag === 'floor') tryClaimCheckpoint(pogo.lastLandingPoint);
  }

  if (heightM > best) {
    best = heightM;
    pbRing.position.y = spawnY + best;
    pbRing.visible = best >= PB_RING_MIN_M;
    bestDirty = true;
  }
  if (bestDirty && nowMs - bestWrittenAt > PB_WRITE_MS) {
    bestWrittenAt = nowMs;
    bestDirty = false;
    settings.best = best;
  }

  // Summit: once per ascent. Falling back down re-arms it.
  if (summitArmed) {
    if (
      pogo.pos.y >= level.topY - SUMMIT_TOLERANCE_M &&
      Math.hypot(pogo.pos.x, pogo.pos.z) < SUMMIT_RADIUS_M
    ) {
      summitArmed = false;
      settings.summitCount = settings.summitCount + 1;
      toast('THE MOUNTAIN FORGIVES.', 'major');
      audio.sting();
    }
  } else if (pogo.pos.y < level.topY - SUMMIT_REARM_M) {
    summitArmed = true;
  }

  // Checkpoint recovery: every fall inside your zone is still yours to eat, but
  // one that drops out the bottom of it ends at the pad you earned.
  const pad = activeCheckpoint >= 0 ? level.checkpoints[activeCheckpoint] : undefined;
  if (pad !== undefined && pogo.pos.y < pad[1] - CHECKPOINT_DROP_M) {
    pogo.respawn();
    orbitCam.reset(pogo.pos);
    toast('Returned to checkpoint', 'minor');
    // The return already *is* the punishment — rebase the high-water mark so the
    // next landing does not bill you for the same metres a second time.
    sessionPeak = pogo.pos.y - spawnY;
  }

  // Void safety: the level has holes, and falling out of the world is not a
  // punishment the design asked for.
  if (pogo.pos.y < VOID_Y) {
    pogo.respawn();
    orbitCam.reset(pogo.pos);
    toast('Out of bounds — back to the plaza', 'minor');
  }

  // --- HUD -----------------------------------------------------------------
  if (started) {
    refreshRoster(nowMs);
    hud.update({
      heightM,
      bestM: best,
      topY: level.topY - spawnY,
      charge: pogo.charge,
      chargeFull: pogo.charge >= 0.999,
      players: ghosts.count() + 1,
      status: netStatus,
      roster: hudRoster,
    });
  }

  if (debug.visible) {
    const v = pogo.vel;
    debug.set({
      pos: `${pogo.pos.x.toFixed(1)} ${pogo.pos.y.toFixed(1)} ${pogo.pos.z.toFixed(1)}`,
      vel: `${v.x.toFixed(1)} ${v.y.toFixed(1)} ${v.z.toFixed(1)}`,
      speed: Math.hypot(v.x, v.y, v.z),
      height: heightM,
      apex: pogo.apexY - spawnY,
      'last bounce': pogo.lastBounceSpeed,
      charge: pogo.charge,
      grounded: pogo.grounded ? 'yes' : 'no',
      fps: Math.round(loop.fps),
      substeps: loop.steps,
      ghosts: ghosts.count(),
      'full rigs': ghosts.fullRigCount(),
      net: netStatus,
    });
  }

  renderer.render(scene, camera);
}

const loop = new FixedLoop(TUNING.FIXED_DT, TUNING.MAX_SUBSTEPS, fixedUpdate, render);
loop.start();
